import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Paper, Switch, Typography,
} from '@mui/material';
import { InstitutionService } from '../../services/institution.service';
import { IModuleAccess } from '../../interfaces/institution.interface';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { GATED_MODULE_KEYS, MODULE_META, MODULES_CHANGED_EVENT } from '../dashboard/registry/moduleMeta';
import { ModuleKey } from '../dashboard/registry/types';
import { helpWhy } from '../../help/why';

/**
 * "Modules" card for the institution settings page. Lists only the modules the platform granted to this
 * institution; the admin can switch any of them off and on again. Hidden if the read fails (the caller
 * lacks the module-manage key).
 */
const ModuleAccessCard: React.FC<{ institutionId: string }> = ({ institutionId }) => {
    const toast = useToast();
    const { refreshPermissions } = useAuth();
    const canManage = usePermission(PERMISSIONS.MODULE_MANAGE);
    const [access, setAccess] = useState<IModuleAccess | null>(null);
    const [saving, setSaving] = useState(false);
    // S4-17 DA-09: switching a module off hides it for the whole company, so it asks first
    const [confirmOff, setConfirmOff] = useState<ModuleKey | null>(null);

    const load = useCallback(async () => {
        try {
            setAccess(await InstitutionService.getModuleAccess(institutionId));
        } catch {
            setAccess(null);
        }
    }, [institutionId]);

    useEffect(() => {
        load();
    }, [load]);

    if (!access) return null;

    // canonical menu order; a granted key the client does not know is left out
    const granted = GATED_MODULE_KEYS.filter(k => access.granted.includes(k));
    const off = new Set(access.disabled);

    const toggle = async (key: ModuleKey, on: boolean) => {
        const next = new Set(off);
        if (on) next.delete(key); else next.add(key);
        try {
            setSaving(true);
            setAccess(await InstitutionService.setModuleAccess(institutionId, Array.from(next)));
            toast.showSuccess(`${MODULE_META[key].label} switched ${on ? 'on' : 'off'}`);
            refreshPermissions();
            window.dispatchEvent(new Event(MODULES_CHANGED_EVENT));
        } catch (error) {
            toast.showApiError(error, 'Failed to save modules');
        } finally {
            setSaving(false);
        }
    };

    const switchWhy = saving || canManage ? null : 'You need the permission to enable and disable modules to change this.';

    return (
        <Paper sx={{ p: 3, mt: 3 }} data-help-section="institutions.modules.section">
            <Typography variant="h6" sx={{ mb: 2 }}>Modules</Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The modules in your subscription. Switch off the ones your company does not use; their menus
                and screens are hidden for everyone. Nothing is deleted, and you can switch a module back on at any time.
            </Typography>

            {granted.length === 0 ? (
                <Alert severity="info">No modules are part of your subscription yet. Ask your provider to add them.</Alert>
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3 }}>
                    {granted.map(key => (
                        <Box key={key} sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                            <FormControlLabel
                                data-help="institutions.modules.switch"
                                {...helpWhy(switchWhy ? { kind: 'status', message: switchWhy } : null)}
                                control={
                                    <Switch
                                        checked={!off.has(key)}
                                        disabled={saving || !canManage}
                                        onChange={(e) => (e.target.checked ? toggle(key, true) : setConfirmOff(key))}
                                    />
                                }
                                label={MODULE_META[key].label}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5, mb: 1 }}>
                                {MODULE_META[key].blurb}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {!canManage && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Only an administrator who can enable and disable modules can change this.
                </Typography>
            )}

            <Dialog open={confirmOff !== null} onClose={() => setConfirmOff(null)} aria-labelledby="module-off-title">
                <DialogTitle id="module-off-title">{confirmOff && `Switch off ${MODULE_META[confirmOff].label}?`}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Its menus and screens disappear for everyone in your company, including people in the middle of
                        work in it. No data is deleted, and you can switch it back on at any time. The change is recorded
                        in the audit log.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOff(null)}>Cancel</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        onClick={() => {
                            const key = confirmOff;
                            setConfirmOff(null);
                            if (key) toggle(key, false);
                        }}
                    >
                        Switch off
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ModuleAccessCard;
